import React from 'react';
import { Video, StopCircle, CheckCircle } from 'lucide-react';
import CameraDevicePicker from '../../CameraDevicePicker';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const ConsentVideoRecorder = ({
  videoRef,
  stream,
  isRecording,
  recordedBlob,
  recordingTime,
  procedureName,
  onStartCamera,
  onStartRecording,
  onStopRecording,
  onNext,
  onStreamReady
}) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-gray-900">Record Video Consent</h3>
    <p className="text-sm text-gray-600">
      Record the patient giving their informed consent. Ensure the patient's face is clearly visible and they verbally confirm their consent.
    </p>

    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
      <div className="font-medium text-yellow-800 mb-1">Script for Recording:</div>
      <ol className="list-decimal list-inside text-yellow-700 space-y-1">
        <li>State patient's full name and date of birth</li>
        <li>Confirm the procedure: "{procedureName}"</li>
        <li>Confirm risks and benefits have been explained</li>
        <li>Confirm all questions have been answered</li>
        <li>Ask patient to verbally state: "I give my consent"</li>
      </ol>
    </div>

    {/* Robust camera picker — works on all laptops, USB cams, phone front/back */}
    {!stream && !recordedBlob && (
      <CameraDevicePicker
        onStreamReady={(s) => {
          if (videoRef?.current) videoRef.current.srcObject = s;
          (onStreamReady || onStartCamera)?.(s);
        }}
        videoConstraints={{ width: { ideal: 1280 }, height: { ideal: 720 } }}
        audioEnabled
        autoStart
      />
    )}

    {(stream || recordedBlob) && (
      <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover"
        />
        {isRecording && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
            <span className="font-mono">{formatTime(recordingTime)}</span>
          </div>
        )}
      </div>
    )}

    <div className="flex justify-center gap-4">
      {stream && !isRecording && !recordedBlob && (
        <button
          onClick={onStartRecording}
          className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <Video className="h-5 w-5" />
          Start Recording
        </button>
      )}
      {isRecording && (
        <button
          onClick={onStopRecording}
          className="flex items-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
        >
          <StopCircle className="h-5 w-5" />
          Stop Recording
        </button>
      )}
      {recordedBlob && (
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <CheckCircle className="h-5 w-5" />
          Review Recording
        </button>
      )}
    </div>
  </div>
);

export { formatTime };
export default ConsentVideoRecorder;
